import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip } from 'recharts';
import { Moon, Plus, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSleepLogs } from '@/hooks/use-sleep';
import { AddSleepDialog } from './AddSleepDialog';
import { format, subDays, startOfDay, addDays } from 'date-fns';
import type { SleepLog } from '@/lib/firebase/queries/sleep';

interface SleepLogsWidgetProps {
  organizationId?: string;
}

export function SleepLogsWidget({ organizationId }: SleepLogsWidgetProps) {
  const { t, dir } = useLanguage();
  const { sleepLogs, loading, addSleepLog, editSleepLog, removeSleepLog } = useSleepLogs();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingSleepLog, setEditingSleepLog] = useState<SleepLog | null>(null);
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week, -1 = previous week, etc.

  const handleDialogSuccess = async (
    dateStr: string,
    sleepTimeStr: string,
    awakenTimeStr: string,
    notes?: string
  ) => {
    // Combine date with time strings
    const sleepDateTime = new Date(`${dateStr}T${sleepTimeStr}`);
    const awakenDateTime = new Date(`${dateStr}T${awakenTimeStr}`);
    
    // If awaken time is before sleep time, it means the user woke up the next day
    if (awakenDateTime < sleepDateTime) {
      awakenDateTime.setDate(awakenDateTime.getDate() + 1);
    }
    
    const sleepDate = startOfDay(sleepDateTime);
    
    if (editingSleepLog) {
      await editSleepLog(editingSleepLog.id, {
        date: sleepDate,
        sleepTime: sleepDateTime,
        awakenTime: awakenDateTime,
        notes,
      });
      setEditingSleepLog(null);
    } else {
      await addSleepLog({
        date: sleepDate,
        sleepTime: sleepDateTime,
        awakenTime: awakenDateTime,
        notes,
      });
      setShowAddDialog(false);
    }
  };

  // Helper function to convert time to minutes since 6pm (18:00)
  // Returns minutes: 0 = 6pm, 1080 = 12pm (18 hours)
  const timeToMinutes = (date: Date): number => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const totalMinutes = hours * 60 + minutes;
    
    // If time is before 6pm (18:00), it's the next day
    // Convert to minutes since 6pm
    if (totalMinutes < 18 * 60) {
      // Next day: add 24 hours
      return (24 * 60 - 18 * 60) + totalMinutes;
    } else {
      // Same day: subtract 6pm
      return totalMinutes - 18 * 60;
    }
  };

  // Helper function to convert minutes since 6pm to Y position (0-1)
  const minutesToYPosition = (minutes: number): number => {
    // Clamp to 0-1080 minutes (6pm to 12pm = 18 hours)
    const clamped = Math.max(0, Math.min(1080, minutes));
    // Convert to 0-1 range (0 = top = 6pm, 1 = bottom = 12pm)
    // 0 minutes (6pm) -> 0 (top), 1080 minutes (12pm) -> 1 (bottom)
    return clamped / 1080;
  };

  // Prepare chart data for the selected week
  const chartData = useMemo(() => {
    const today = startOfDay(new Date());
    // Calculate the start of the week (Sunday)
    const currentDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const currentWeekStart = addDays(today, -currentDayOfWeek);
    const weekStart = addDays(currentWeekStart, weekOffset * 7);
    const days = [];
    
    // Generate 7 days for the selected week
    for (let i = 0; i < 7; i++) {
      const date = addDays(weekStart, i);
      const dateStart = startOfDay(date);
      
      // Find sleep log for this date
      const log = sleepLogs.find(log => {
        const logDate = startOfDay(new Date(log.date));
        return logDate.getTime() === dateStart.getTime();
      });
      
      if (log) {
        const sleepMinutes = timeToMinutes(log.sleepTime);
        const awakenMinutes = timeToMinutes(log.awakenTime);
        const sleepY = minutesToYPosition(sleepMinutes);
        const awakenY = minutesToYPosition(awakenMinutes);
        
        // Bar height (in percentage) - from sleep time to awaken time
        const barHeight = Math.abs(sleepY - awakenY);
        // Bar top position (start of sleep)
        const barTop = Math.min(sleepY, awakenY);
        
        days.push({
          day: format(date, 'EEE'),
          date: format(date, 'MM/dd'),
          hours: log.hours,
          fullDate: format(date, 'yyyy-MM-dd'),
          sleepTime: log.sleepTime,
          awakenTime: log.awakenTime,
          sleepY,
          awakenY,
          barHeight,
          barTop,
          hasData: true,
          log: log, // Store the full log for editing
        });
      } else {
        days.push({
          day: format(date, 'EEE'),
          date: format(date, 'MM/dd'),
          hours: 0,
          fullDate: format(date, 'yyyy-MM-dd'),
          hasData: false,
        });
      }
    }
    
    return days;
  }, [sleepLogs, weekOffset]);

  // Custom shape function to render bars at specific Y positions
  const renderCustomBar = (props: any) => {
    const { payload, x, width } = props;
    
    if (!payload || !payload.hasData) return null;
    
    const chartAreaHeight = 200;
    const marginTop = 10;
    const marginBottom = 10;
    const actualChartHeight = chartAreaHeight - marginTop - marginBottom;
    
    const barHeight = payload.barHeight * actualChartHeight;
    const barY = marginTop + (payload.barTop * actualChartHeight);
    
    // Make bars narrower - 50% of previous width
    const barWidthRatio = width > 100 ? 0.325 : width > 60 ? 0.3 : 0.275;
    const barWidth = width * barWidthRatio;
    const marginRatio = (1 - barWidthRatio) / 2;
    const barX = x + (width * marginRatio);
    
    return (
      <rect
        x={barX}
        y={barY}
        width={barWidth}
        height={Math.max(barHeight, 2)}
        fill="#FF8C42"
        rx={4}
        ry={4}
        style={{ cursor: 'pointer' }}
        onClick={() => {
          if (payload.log) {
            setEditingSleepLog(payload.log);
          }
        }}
      />
    );
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b bg-muted/50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Moon className="h-5 w-5 text-orange-500" />
            {t('dashboard.customer.dashboard.sleepLogs.title') || 'Sleep Logs'}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddDialog(true)}
            className="bg-white hover:bg-gray-100"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">{t('sleep.addSleepLog') || 'Add Sleep Hours'}</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Chart */}
            <div className="w-full">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData} margin={{ top: 10, right: 5, left: 5, bottom: 10 }}>
                  <XAxis 
                    dataKey="day" 
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    domain={[0, 1]}
                    tick={{ fontSize: 10, textAnchor: dir === 'rtl' ? 'start' : 'end' }}
                    tickLine={false}
                    axisLine={false}
                    width={60}
                    tickMargin={5}
                    tickFormatter={(value) => {
                      // Convert Y position (0-1) back to time
                      // 0 = 6pm (18:00), 1 = 12pm (12:00)
                      const minutes = Math.round((1 - value) * 1080); // 1080 minutes = 18 hours
                      const hours = Math.floor(minutes / 60);
                      const mins = minutes % 60;
                      const displayHours24 = (18 + hours) % 24;
                      // Convert to 12-hour format
                      let displayHours12: number;
                      if (displayHours24 === 0) {
                        displayHours12 = 12; // 12:00 AM (midnight)
                      } else if (displayHours24 === 12) {
                        displayHours12 = 12; // 12:00 PM (noon)
                      } else if (displayHours24 > 12) {
                        displayHours12 = displayHours24 - 12;
                      } else {
                        displayHours12 = displayHours24;
                      }
                      const ampm = displayHours24 >= 12 ? 'PM' : 'AM';
                      // Use non-breaking space to prevent wrapping
                      // Only show minutes if they're not :00
                      const timeStr = mins === 0 ? `${displayHours12}` : `${displayHours12}:${String(mins).padStart(2, '0')}`;
                      return `${timeStr}\u00A0${ampm}`;
                    }}
                    ticks={[0, 0.333, 0.667, 1]} // 6pm, 12am, 6am, 12pm
                  />
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke="#9ca3af" 
                    horizontal={true}
                    vertical={false}
                    strokeOpacity={0.6}
                  />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        if (!data.hasData) return null;
                        return (
                          <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
                            <div className="flex flex-col gap-1">
                              <p className="text-sm font-medium">{data.date}</p>
                              <p className="text-sm text-muted-foreground">
                                {format(data.sleepTime, 'p')} - {format(data.awakenTime, 'p')}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {data.hours.toFixed(1)} {t('dashboard.customer.dashboard.sleepLogs.hours') || 'hours'}
                              </p>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar 
                    dataKey="barHeight"
                    shape={renderCustomBar}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Week Navigator */}
            <div className="flex items-center justify-between pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setWeekOffset(weekOffset - 1)}
                className="gap-2"
              >
                {dir === 'rtl' ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                {t('common.previous') || 'Previous'}
              </Button>
              
              <div className="text-sm font-medium text-muted-foreground">
                {weekOffset === 0 ? (
                  t('sleep.thisWeek') || 'This Week'
                ) : weekOffset === -1 ? (
                  t('sleep.lastWeek') || 'Last Week'
                ) : weekOffset < 0 ? (
                  `${Math.abs(weekOffset)} ${t('sleep.weeksAgo') || 'weeks ago'}`
                ) : (
                  t('sleep.futureWeek') || 'Future Week'
                )}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setWeekOffset(weekOffset + 1)}
                disabled={weekOffset >= 0}
                className="gap-2"
              >
                {t('common.next') || 'Next'}
                {dir === 'rtl' ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <AddSleepDialog
        open={showAddDialog || !!editingSleepLog}
        onOpenChange={(open) => {
          if (!open) {
            setShowAddDialog(false);
            setEditingSleepLog(null);
          }
        }}
        onSuccess={handleDialogSuccess}
        sleepLog={editingSleepLog}
      />
    </Card>
  );
}
